import { AdornedInput, Box, Paragraph, Popover, Text, Tooltip } from '@zillow/constellation-10';
import { IconClockFilled } from '@zillow/constellation-icons';
import { useContext, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { IsVisible } from '@zg-rentals/react-hooks';
import { isEqual } from 'es-toolkit';
import StringConstants from '../../constants/StringConstants';
import { AppContext } from '../../contexts/AppContextProvider';
import dateTimeUtils from '../../utils/dateTime/dateTimeUtils';
import { highlightedTimeValueStyles, labelStyles, timeValueStyles } from './styles';

type Props = {
  onTimePickerChange(tourScheduledTimestampMs: string): void;
  tourScheduledTimestampMs: string | undefined | null;
};

type TimeValues = {
  hour: string | undefined;
  minute: string | undefined;
  meridiem: string | undefined;
};

const HOUR_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const MINUTE_OPTIONS = ['00', '15', '30', '45'];
const MERIDIEM_OPTIONS = ['am', 'pm'];
const TIME_PICKER_FORMAT = 'h:mm aaa';

const { getTimestampMsFromTimeValues, getTimeValuesFromTimestampMs } = dateTimeUtils;

const TimePicker = ({ onTimePickerChange, tourScheduledTimestampMs }: Props): JSX.Element => {
  const { isImpersonator } = useContext(AppContext);
  const [currentTimestampMs, setCurrentTimestampMs] = useState(tourScheduledTimestampMs);

  let formattedTimestampMs: string | undefined;
  if (currentTimestampMs) {
    formattedTimestampMs = format(+currentTimestampMs, TIME_PICKER_FORMAT);
  }

  const [timeValues, setTimeValues] = useState<TimeValues>(getTimeValuesFromTimestampMs(formattedTimestampMs));

  useEffect(() => {
    if (tourScheduledTimestampMs) {
      const formattedTimestampMs = format(+tourScheduledTimestampMs, TIME_PICKER_FORMAT);
      const formattedValueFromTimeStampMs = getTimeValuesFromTimestampMs(formattedTimestampMs);
      setTimeValues(formattedValueFromTimeStampMs);
    }
    setCurrentTimestampMs(tourScheduledTimestampMs);
  }, [tourScheduledTimestampMs]);

  const handleOnClick = (timeValueToUpdate: Partial<TimeValues>): void => {
    const newTimeValues = {
      ...timeValues,
      ...timeValueToUpdate,
    };

    // Nothing to update
    if (isEqual(newTimeValues, timeValues)) {
      return;
    }

    const newTimestampMs = getTimestampMsFromTimeValues({
      newTimeValues,
      currentTimestampMs,
    });

    setTimeValues(newTimeValues);
    setCurrentTimestampMs(newTimestampMs);
    onTimePickerChange(newTimestampMs);
  };

  if (!tourScheduledTimestampMs || isImpersonator) {
    return <TooltipContent isImpersonator={isImpersonator} />;
  }

  return (
    <Popover
      header={
        <Box css={{ display: 'flex', flexDirection: 'row' }}>
          <Paragraph textStyle="body-bold" css={labelStyles}>
            Hr
          </Paragraph>

          <Paragraph textStyle="body-bold" css={labelStyles}>
            Min
          </Paragraph>
          <Box css={labelStyles}>
            <span>{/* placeholder for alignment */}</span>
          </Box>
        </Box>
      }
      body={
        <Box css={{ display: 'flex', flexDirection: 'row' }}>
          <Box css={labelStyles}>
            {HOUR_OPTIONS.map((hour) => {
              const isHighlighted = timeValues.hour && timeValues.hour === hour;

              return (
                <Box
                  key={hour}
                  onClick={() => {
                    handleOnClick({ hour });
                  }}
                  css={{
                    ...timeValueStyles,
                    ...(isHighlighted && highlightedTimeValueStyles),
                  }}
                >
                  <Text>{hour}</Text>
                </Box>
              );
            })}

            <IsVisible options={{ rootMargin: '0px 0px -80px 0px' }}>
              {({ ref }) => <Box ref={ref} css={{ height: '1px' }} />}
            </IsVisible>
          </Box>

          <Box css={labelStyles}>
            {MINUTE_OPTIONS.map((minute) => {
              const isHighlighted = timeValues.minute && timeValues.minute === minute;

              return (
                <Box
                  key={minute}
                  onClick={() => {
                    handleOnClick({ minute });
                  }}
                  css={{
                    ...timeValueStyles,
                    ...(isHighlighted && highlightedTimeValueStyles),
                  }}
                >
                  <Text>{`:${minute}`}</Text>
                </Box>
              );
            })}
          </Box>

          <Box css={labelStyles}>
            {MERIDIEM_OPTIONS.map((meridiem) => {
              const isHighlighted = timeValues.meridiem && timeValues.meridiem === meridiem;

              return (
                <Box
                  key={meridiem}
                  onClick={() => {
                    handleOnClick({ meridiem });
                  }}
                  css={{
                    ...timeValueStyles,
                    ...(isHighlighted && highlightedTimeValueStyles),
                  }}
                >
                  <Text>{meridiem}</Text>
                </Box>
              );
            })}
          </Box>
        </Box>
      }
      trigger={
        <AdornedInput
          input={<AdornedInput.Input value={formattedTimestampMs} readOnly />}
          endAdornment={
            <AdornedInput.Adornment>
              <AdornedInput.Icon css={{ color: 'icon.neutral' }}>
                <IconClockFilled />
              </AdornedInput.Icon>
            </AdornedInput.Adornment>
          }
          css={{
            paddingRight: 'tight',
          }}
        />
      }
    />
  );
};

const TooltipContent = ({ isImpersonator }: { isImpersonator: boolean }): JSX.Element => {
  const tooltipText = isImpersonator ? StringConstants.TOOLTIP.IMPERSONATION : 'Please provide a date first.';

  return (
    <Tooltip
      content={tooltipText}
      trigger={
        <AdornedInput
          disabled={true}
          input={<AdornedInput.Input placeholder="00:00 am" />}
          endAdornment={
            <AdornedInput.Adornment>
              <AdornedInput.Icon>
                <IconClockFilled />
              </AdornedInput.Icon>
            </AdornedInput.Adornment>
          }
          css={{
            paddingRight: 'tight',
          }}
        />
      }
    />
  );
};

export default TimePicker;
